import { DateOnly } from '@if/core/date'
import { EFinancingProgram, EPlanType, EWorksheetDecision } from '@if/core/types'
import { Grid, Stack } from '@mui/material'
import { useCreditApplicationContext, useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useGetAvailableCredit } from '@src/data/api/available-credit-api'
import { useMerchantFinancingConfig } from '@src/data/api/config/financing-config-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useBusinessErrors, useFinancingConfig } from '@src/data/selectors/app-config-selectors'
import {
  useAreAllTasksRequiredForCVTCompleted,
  useConfirmAllIncomesStatus,
  useCurrentCreditApplicationSpecificNorms,
  useIsFinalDecisionPending,
} from '@src/data/selectors/credit-selectors'
import { useAreAllDocumentsRequiredForCVT } from '@src/data/selectors/document-selectors'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import translate from '@src/services/translate'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Breadcrumb, InputTextField, PageError, PageSpinner } from '../../../components'
import PendingMessage from '../../../components/PendingMessage'
import { useComputeB2cFunding, useUpdateB2cWorksheetDecision } from '../../../data/api/b2c-worksheet/b2c-worksheet-api'
import { BaseWorksheetFundingThresholdExtend, NormsMessage, WorksheetDecision } from '../../../data/types'
import { B2cWorksheet, B2cWorksheetDecisionDTO } from '../../../data/types/B2cWorksheetSchema'
import WarningMessageAccordion from '../../ViewCreditApplicationPage/components/warningMessageAccordion'
import LoanSummaryBox from '../components/LoanSummary/LoanSummaryBox'
import WorksheetDecisionDialog from '../components/LoanSummary/WorksheetDecisionDialog'
import WorksheetDecisionSection from '../components/LoanSummary/WorksheetDecisionSection'
import TransactionSection from '../components/Transaction/TransactionSection'
import { useDialogDecisionDefaultValue, useViewTotalObligation } from '../viewWorksheet-hooks'
import { canComputeFunding, getEmptyComputedFundingDto } from '../viewWorksheet-selectors'

const B2cWorksheetViewPage = () => {
  const { t } = useTranslation()

  const { creditApplication, worksheet, isLoading } = useCreditApplicationData()
  const b2cWorksheet = worksheet as B2cWorksheet | undefined

  const [currentOpenDialog, setCurrentOpenDialog] = useState<string>('')

  const [computedInfo, setComputedInfo] = useState<FundingComputedDto>({} as FundingComputedDto)

  const [computeFunding] = useComputeB2cFunding()
  const [updateDecision] = useUpdateB2cWorksheetDecision()

  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()
  const error = useBusinessErrors()
  const commonNormsMessage: NormsMessage[] = useCurrentCreditApplicationSpecificNorms()

  const programFinancingConfig = useFinancingConfig(creditApplication?.financingProgramId)
  const [merchantFinancingConfig] = useMerchantFinancingConfig(
    creditApplication?.merchantId,
    creditApplication?.financingProgramId,
  )
  const financingConfig = merchantFinancingConfig ? merchantFinancingConfig : programFinancingConfig

  const { id, financingProgramId } = useCreditApplicationContext()
  const [availableCreditData] = useGetAvailableCredit(financingProgramId, id)
  const availableCredit = availableCreditData?.availableAmount ?? 0
  const creditInProgress = availableCreditData?.creditInProgress ?? 0

  const totalObligation = useViewTotalObligation(b2cWorksheet?.amountRequested, computedInfo)

  const user = useUser()

  const worksheetNormWarningMessages = useMemo(() => {
    if (!b2cWorksheet?.normsDecision?.warningMessages) return []
    return b2cWorksheet.normsDecision.warningMessages.map((wr) => {
      const translatedMessage = translate([`worksheet.norms.${wr.id}`])
      let messageSuffix = ' | '
      wr.parameters.forEach((p, i) => {
        if (i >= 1) messageSuffix = messageSuffix.concat(', ')
        const suffix = translate(`worksheet.paramNames.${p.name}`, { val: p.value })
        messageSuffix = messageSuffix.concat(suffix)
      })
      return {
        id: wr.id,
        message: translatedMessage.concat(messageSuffix),
        applicantType: null,
      } as NormsMessage
    })
  }, [b2cWorksheet?.normsDecision?.warningMessages])

  const { documents, requiredDocuments } = useCreditApplicationData()
  const areAllTasksRequiredForCVTCompleted = useAreAllTasksRequiredForCVTCompleted()
  const areAllDocumentsRequiredForCVTApproved = useAreAllDocumentsRequiredForCVT(documents, requiredDocuments)
  const confirmAllIncomesStatus = useConfirmAllIncomesStatus()
  const finalDecisionIsPending = useIsFinalDecisionPending()
  const planName = creditApplication?.normsCreditDecision?.planName ?? EPlanType.Default

  const dialogDecisionDefaultValue = useDialogDecisionDefaultValue(
    creditApplication!,
    b2cWorksheet!,
    areAllTasksRequiredForCVTCompleted,
    areAllDocumentsRequiredForCVTApproved,
    confirmAllIncomesStatus!,
  )

  const editDisabled = creditApplication?.editLocked || finalDecisionIsPending

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.B2c}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  const EditDecisionDialogOpen = 'EditDecisionDialogOpen'

  useEffect(() => {
    if (creditApplication && b2cWorksheet && Object.keys(computedInfo).length === 0) {
      const interestRate = creditApplication.finalCreditDecision.interestRate ?? 0
      const stateIso = creditApplication.applicant.currentAddress.stateIso
      const requestId = nanoid()

      const computeDto: FundingInfoDto = {
        financingProgramId: creditApplication.financingProgramId,
        amountRequested: b2cWorksheet.amountRequested ?? 0,
        includeInsurance: b2cWorksheet.includeInsurance,
        paymentFrequency: b2cWorksheet.paymentFrequency,
        paymentPlanId: b2cWorksheet.paymentPlanId,
        term: b2cWorksheet.term,
        interestRate,
        hasCoApplicant: false,
        stateIso: stateIso,
        deliveryOn: new DateOnly(b2cWorksheet.deliveryOn),
        firstPaymentOn: b2cWorksheet.firstPaymentOn,
        requestId,
        creditApplicationId: creditApplication.id,
        merchantId: creditApplication.merchantId,
        insuranceCompanyId: b2cWorksheet.insuranceCompanyId,
      }

      if (canComputeFunding(computeDto, financingConfig.minimumLoanAmount)) {
        const fetchFunding = async () => {
          try {
            const data = await computeFunding(computeDto)
            setComputedInfo(data)
          } catch (error) {
            reportErrorToConsole(error)
          }
        }
        void fetchFunding()
      } else {
        const emptyComputedDto = getEmptyComputedFundingDto()
        setComputedInfo(emptyComputedDto)
      }
    }
  }, [b2cWorksheet, computedInfo, creditApplication, financingConfig.minimumLoanAmount, computeFunding])

  // callbacks
  const closeDialogs = useCallback(() => {
    setCurrentOpenDialog('')
  }, [])

  const handleSaveDecision = useCallback(
    async (data: WorksheetDecision) => {
      if (b2cWorksheet) {
        const updatedFinalDecision = {
          creditApplicationId: b2cWorksheet.creditApplicationId,
          ...b2cWorksheet.finalDecision,
          versionTag: b2cWorksheet.versionTag,
          fundingThresholdExtend: null,
          decision: data.decision,
          otherReason: data.otherReason,
        } as B2cWorksheetDecisionDTO

        if (data.decision === EWorksheetDecision.ApprovedWithCondition) {
          const fundingThresholdExtend: BaseWorksheetFundingThresholdExtend = {
            maxPmtAmount: data.maxPmtAmount,
            maxTotalAmountFinanced: data.maxTotalAmountFinanced,
            maxTermDuration: data.maxTermDuration,
          }
          updatedFinalDecision.fundingThresholdExtend = fundingThresholdExtend
        }

        try {
          await updateDecision({
            ...updatedFinalDecision,
            creditApplicationId: b2cWorksheet?.creditApplicationId,
          })
          closeDialogs()
          await invalidateFullCreditApplication(creditApplication!.id, creditApplication!.financingProgramId)
        } catch (error) {
          reportErrorToConsole(error)
        }
      }
    },
    [b2cWorksheet, closeDialogs, creditApplication, invalidateFullCreditApplication, updateDecision],
  )

  return (
    <div>
      <PageSpinner isLoading={isLoading} />
      {!isLoading && b2cWorksheet && creditApplication && (
        <>
          <Breadcrumb trees={breadCrumbs} />
          <Stack>
            <PageError errors={error} />
            {finalDecisionIsPending && <PendingMessage />}
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <InputTextField value={`${planName}`} label={t('worksheetCommon.program')} disabled />
              <InputTextField
                label={t('worksheetCommon.creditLimit')}
                disabled
                value={creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0}
              />
              <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />

              <InputTextField label={t('worksheetCommon.availableCredit')} disabled value={availableCredit} />
            </Stack>
            {commonNormsMessage?.length > 0 && (
              <WarningMessageAccordion
                applicantCreditWarnings={null}
                coapplicantCreditWarnings={null}
                commonCreditWarnings={commonNormsMessage}
                worksheetWarnings={null}
                expanded={false}
              />
            )}
            <Grid container textAlign="center" justifyContent="space-between" p={4}>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <LoanSummaryBox worksheet={b2cWorksheet} computedInfo={computedInfo} merchantPaymentPlan={null} />
              </Grid>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <WorksheetDecisionSection
                  worksheet={b2cWorksheet}
                  onClick={() => setCurrentOpenDialog(EditDecisionDialogOpen)}
                  editDisabled={editDisabled}
                  userCanEditWorksheet={user?.rights.canEditWorksheet === true}
                />
              </Grid>
            </Grid>
            <TransactionSection
              worksheet={b2cWorksheet}
              creditApplication={creditApplication}
              totalObligation={totalObligation}
              merchantPaymentPlan={null}
              paymentMerchantsByIds={{}}
              productsPlans={[]}
            />
          </Stack>

          <WorksheetDecisionDialog
            open={currentOpenDialog === EditDecisionDialogOpen}
            title={t('worksheet.decision')}
            onCancel={closeDialogs}
            onConfirm={handleSaveDecision}
            worksheetWarnings={worksheetNormWarningMessages}
            defaultValue={dialogDecisionDefaultValue}
            worksheet={b2cWorksheet}
            creditapplication={creditApplication}
          />
        </>
      )}
    </div>
  )
}

export default B2cWorksheetViewPage
